import { useEffect } from 'react';
import { yomiriP2P } from './YomiriP2PBridge.ts';
import { getRemoteSnapshot, setRemoteSnapshot, type CastSnapshot } from './RemoteSession.ts';
import { sourceProvider } from '../core/SourceProvider.ts';
import { createAnimeSource, installedAnimeSources } from '../anime/AnimeRepository.ts';
import { getNowFutbol, installedLiveSources } from '../live/NowFutbol.ts';
import { fetchArgentinaLiveStatus } from '../live/ArgentinaChannels.ts';
import { sourceHttpClient } from '../core/SourceHttp.ts';
import { repositoryIds, readStored } from '../repositories/RepositoryManager.ts';
import { storedRepositoryEntries } from '../repositories/RepositoryStore.ts';
import { yomiriDatabase, type YomiriChapterState, type YomiriHistoryEntry } from '../storage/YomiriDatabase.ts';
import { fetchPageBlob } from '../storage/LocalChapters.ts';
import type { YomiriPage } from '../types/extension.ts';
import { tvPairingSync } from '../network/pairing/TvPairingSync.ts';

async function snapshot():Promise<CastSnapshot>{
  const names=readStored<Record<string,string>>('yomiri_repository_names',{});
  const manga=sourceProvider.getEnabledExtensions(),anime=await installedAnimeSources(),live=await installedLiveSources();
  const installed=new Set([...manga.map(source=>source.id),...anime,...live]);
  return {version:1,repositories:repositoryIds().map(url=>({url,name:names[url]||url})),
    manga:manga.map(source=>({
      id:source.id,name:source.name,lang:source.lang,version:source.version,
      iconUrl:source.iconUrl||'',baseUrl:source.website||'',
    })),anime,live,
    entries:Object.values(storedRepositoryEntries()).flat().filter(entry=>installed.has(entry.nativeId||entry.source.connectorId||entry.runtimeId))
      .map(entry=>({...entry,extension:{...entry.extension,sources:[]},source:{
        id:entry.source.id,name:entry.source.name,lang:entry.source.lang,baseUrl:entry.source.baseUrl,
        contentKind:entry.source.contentKind,connectorId:entry.source.connectorId,
      }}))};
}
const mangaOps=['search','popular','latest','detail','chapters','pages','filters'];
const animeOps=['search','getPopular','getLatest','getAnime','getEpisodes','getServers','getSearchFilters'];
async function serve(method:string,input:unknown):Promise<unknown>{
  if(yomiriP2P.getRole()!=='remote')throw new Error('El móvil no está disponible.');
  const data=input&&typeof input==='object'?input as {sourceId?:string;args?:unknown[];url?:string;headers?:Record<string,string>}: {};
  const args=Array.isArray(data.args)&&data.args.length<=5?data.args:[];
  const id=String(data.sourceId||'');
  if(method.startsWith('manga.')&&mangaOps.includes(method.slice(6))){
    if(!sourceProvider.isSourceEnabled(id))throw new Error('Esta fuente no está activada en el móvil.');
    const source=sourceProvider.getExtension(id)!;
    switch(method){
      case 'manga.search':return sourceProvider.search(id,String(args[0]||'').slice(0,300),Number(args[1])||1,args[2]as Record<string,unknown>);
      case 'manga.popular':return sourceProvider.getPopular(id,Number(args[0])||1);
      case 'manga.latest':return sourceProvider.getLatest(id,Number(args[0])||1);
      case 'manga.detail':return sourceProvider.getManga(id,String(args[0]));
      case 'manga.chapters':return sourceProvider.getChapters(id,String(args[0]));
      case 'manga.pages':return sourceProvider.getPages(id,String(args[0]));
      case 'manga.filters':return source.getSearchFilters?source.getSearchFilters():[];
    }
  }
  if(method.startsWith('anime.')&&animeOps.includes(method.slice(6))){
    if(!(await installedAnimeSources()).includes(id))throw new Error('Esta fuente de anime no está instalada en el móvil.');
    const source=createAnimeSource(id,sourceHttpClient);
    const operation=source[method.slice(6) as keyof typeof source];
    return typeof operation==='function'?Reflect.apply(operation,source,args):[];
  }
  if(method==='live.feed'&&(await installedLiveSources()).includes('nowfutbol'))return getNowFutbol();
  if(method==='live.argentina'&&(await installedLiveSources()).includes('argentinatv'))return fetchArgentinaLiveStatus();
  if(method==='library.manga')return yomiriDatabase.getLibraryMangas();
  if(method==='library.history')return yomiriDatabase.getRecentHistory(Math.max(1,Math.min(1000,Number(args[0])||50)));
  if(['library.chapter','library.chapters','library.progress'].includes(method)&&sourceProvider.isSourceEnabled(id)){
    if(method==='library.chapter')return yomiriDatabase.getChapterState(id,String(args[0]));
    if(method==='library.chapters')return [...(await yomiriDatabase.getAllChapterStatesForManga(id,String(args[0]))).entries()];
    const input=args[2]&&typeof args[2]==='object'?args[2]as Partial<YomiriChapterState>:{};
    const patch:Partial<YomiriChapterState>={};
    if(typeof input.isRead==='boolean')patch.isRead=input.isRead;
    if(typeof input.isBookmarked==='boolean')patch.isBookmarked=input.isBookmarked;
    if(Number.isInteger(input.lastPageRead)&&input.lastPageRead!>=0&&input.lastPageRead!<100000)patch.lastPageRead=input.lastPageRead;
    return yomiriDatabase.saveChapterState(id,String(args[0]),String(args[1]),patch);
  }
  if(method==='library.record-history'){
    const entry=args[0]as YomiriHistoryEntry;
    if(!entry||!sourceProvider.isSourceEnabled(entry.sourceId)||typeof entry.mangaId!=='string'||typeof entry.chapterId!=='string')throw new Error('Historial no válido.');
    return yomiriDatabase.recordHistory(entry);
  }
  if(method==='library.setting'&&typeof args[0]==='string'&&(
    ['anime_library','anime_watch_progress','anime_watched','anime_history'].includes(args[0])||/^source_languages_[a-z0-9_-]+$/.test(args[0])
  ))return yomiriDatabase.getSetting(args[0]);
  if(method==='library.setting-write'){
    const key=String(args[0]),value=args[1];
    if(!['anime_watched','anime_library','anime_history'].includes(key)||!value||typeof value!=='object'||JSON.stringify(value).length>1000000)throw new Error('Estado de anime inválido.');
    if(key==='anime_watched'&&(Array.isArray(value)||Object.values(value).some(item=>typeof item!=='boolean')))throw new Error('Progreso de anime inválido.');
    if(key!=='anime_watched'){
      if(!Array.isArray(value)||value.length>1000)throw new Error('Biblioteca de anime inválida.');
      const installed=await installedAnimeSources();
      if(value.some(item=>!item||typeof item!=='object'||!installed.includes(item.sourceId)))throw new Error('Fuente de anime no instalada.');
    }
    await yomiriDatabase.setSetting(key,value);window.dispatchEvent(new Event('yomiri-library-changed'));return true;
  }
  if(method==='media.page'){
    const page=(input as {page?:YomiriPage})?.page;
    if(!page||!page.sourceId||!sourceProvider.isSourceEnabled(page.sourceId))throw new Error('Página de una fuente no instalada.');
    const blob=await fetchPageBlob(page);
    if(blob.size>8*1024*1024||!blob.type.startsWith('image/'))throw new Error('Imagen no compatible con la TV.');
    const bytes=new Uint8Array(await blob.arrayBuffer());let binary='';
    for(let offset=0;offset<bytes.length;offset+=8192)binary+=String.fromCharCode(...bytes.subarray(offset,offset+8192));
    return {base64:btoa(binary),mime:blob.type};
  }
  if(method==='media.image'){
    if(!sourceProvider.isSourceEnabled(id)&&!(await installedAnimeSources()).includes(id))throw new Error('Fuente de imagen no instalada.');
    const headers=Object.fromEntries(Object.entries(data.headers||{}).filter(([key])=>['referer','accept'].includes(key.toLowerCase())));
    const response=await sourceHttpClient.bytes({sourceId:id,url:String(data.url||''),headers,method:'GET',
      responseType:'bytes',credentials:'omit',purpose:'image',cache:{ttlMs:60000,scope:'session'}});
      if(response.status!==200)throw new Error('No se pudo obtener la imagen en el móvil.');
      const blob=new Blob([response.body.slice().buffer as ArrayBuffer],{type:response.headers['content-type']||'image/jpeg'});
      if(blob.size>8*1024*1024||!blob.type.startsWith('image/'))throw new Error('La imagen no es compatible con la transmisión a TV.');
      const bytes=new Uint8Array(await blob.arrayBuffer());let binary='';
      for(let offset=0;offset<bytes.length;offset+=8192)binary+=String.fromCharCode(...bytes.subarray(offset,offset+8192));
      return {base64:btoa(binary),mime:blob.type};
  }
  throw new Error('Operación no admitida desde la TV.');
}
export function CastCoordinator({route,remote}:{route:string;remote:boolean}){
  useEffect(()=>{
    const release=yomiriP2P.onRequest(serve);
    let alive=true,timer:ReturnType<typeof setTimeout>|undefined;
    const sync=()=>{
      if(yomiriP2P.getRole()!=='remote'||yomiriP2P.getState()!=='connected')return;
      clearTimeout(timer);
      timer=setTimeout(()=>{
        void snapshot().then(value=>{
          if(alive){
            yomiriP2P.send({type:'STATE_SYNC',snapshot:value});
            yomiriP2P.send({type:'ROUTE_SYNC',search:location.search});
          }
        }).catch(()=>{});
      },100);
    };
    const state=yomiriP2P.onState(value=>{if(value==='connected')sync();else if(getRemoteSnapshot()||!remote)setRemoteSnapshot(null);});
    const commands=yomiriP2P.onCommand(command=>{
      if(yomiriP2P.getRole()!=='tv')return;
      if(command.type==='STATE_SYNC')setRemoteSnapshot(command.snapshot);
      if(command.type==='ROUTE_SYNC'){
        const parsed=new URLSearchParams(command.search);const clean=new URLSearchParams();
        for(const key of ['section','source','manga','chapter','animeSource','anime','episode','liveSource','liveChannel','liveEvent','liveSignal']){const value=parsed.get(key);if(value&&value.length<8192)clean.set(key,value);}
        if(new URLSearchParams(location.search).has('receiver'))clean.set('receiver','1');
        history.pushState(null,'',location.pathname+'?'+clean);window.dispatchEvent(new PopStateEvent('popstate'));
        tvPairingSync.confirmReception('La TV recibió el contenido.');
      }
    });
    const onPlaybackStarted = (event: any) => {
      if (yomiriP2P.getRole() === 'tv' && yomiriP2P.getState() === 'connected') {
        const msg = event?.detail?.message || 'Reproduciendo en la TV.';
        tvPairingSync.confirmPlayback(msg);
      }
    };
    const onVideoPlaying = () => {
      if (yomiriP2P.getRole() === 'tv' && yomiriP2P.getState() === 'connected') {
        tvPairingSync.confirmPlayback('Reproduciendo en la TV.');
      }
    };
    window.addEventListener('yomiri-playback-started', onPlaybackStarted);
    window.addEventListener('playing', onVideoPlaying, true);
    window.addEventListener('yomiri-sources-changed',sync);window.addEventListener('yomiri-library-changed',sync);sync();
    let lastRoute=location.search;
    const routes=setInterval(()=>{if(remote&&yomiriP2P.getRole()==='remote'&&yomiriP2P.getState()==='connected'&&lastRoute!==location.search){
      lastRoute=location.search;tvPairingSync.markSending('Enviando ruta a la TV…');yomiriP2P.send({type:'ROUTE_SYNC',search:lastRoute});}},500);
    return()=>{alive=false;clearTimeout(timer);clearInterval(routes);release();state();commands();window.removeEventListener('yomiri-playback-started', onPlaybackStarted);window.removeEventListener('playing', onVideoPlaying, true);window.removeEventListener('yomiri-sources-changed',sync);window.removeEventListener('yomiri-library-changed',sync);};
  },[remote]);
  useEffect(()=>{
    if(remote&&yomiriP2P.getRole()==='remote'&&yomiriP2P.getState()==='connected'){tvPairingSync.markSending('Enviando a la TV…');yomiriP2P.send({type:'ROUTE_SYNC',search:route});}
  },[remote,route]);
  return null;
}
